import { ArsText } from "../inputs";
import { NamedObjectControl } from "./NamedObjectControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the AnalysisOutputCodeParameterControl component.
 */
export interface AnalysisOutputCodeParameterControlProps {
  /** RFC 6901 JSON Pointer path to the AnalysisOutputCodeParameter in ArsContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Sibling count. */
  siblingCount: number;
}

/**
 * A component for managing AnalysisOutputCodeParameter entities.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the AnalysisOutputCodeParameter in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.siblingCount - Number of sibling items.
 * @returns A React functional component rendering the analysis output code parameter editor.
 */
export const AnalysisOutputCodeParameterControl = ({
  dataPath,
  disabled = false,
  siblingCount,
}: AnalysisOutputCodeParameterControlProps): React.ReactElement => {
  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute top-0 end-0 m-2">
        <RemoveArsItemButton
          dataPath={dataPath}
          disabled={disabled}
          siblingCount={siblingCount}
        />
      </div>

      <NamedObjectControl
        dataPath={dataPath}
        canRename={true}
        disabled={disabled}
      />

      <ArsText
        dataPath={`${dataPath}/value`}
        id={`${idRoute}-value`}
        label="Value(s)"
        labelWidth={2}
        placeholder="Enter one value per line"
        isArray
        type="textarea"
        debounceMilliseconds={2000}
        rows={3}
        disabled={disabled}
      />
    </div>
  );
};

AnalysisOutputCodeParameterControl.displayName = "AnalysisOutputCodeParameterControl";
